import React, {
  Fragment,
  useEffect,
  useReducer,
  useRef,
  useCallback,
  useMemo,
} from "react";
import StickyHeaderDataTable from "../shared/sticky-header-data-table";
import { toast } from "react-toastify";
import { useAuth0 } from "@auth0/auth0-react";
import moment from "moment-timezone";
import Select from "react-select";
import { Eventcalendar, Popup, Button } from "@mobiscroll/react";
import Checkbox from "@mui/material/Checkbox";
import CheckBoxIcon from "@mui/icons-material/CheckBox";
import CheckBoxOutlineBlank from "@mui/icons-material/CheckBoxOutlineBlank";
import ToggleButton from "@mui/material/ToggleButton";
import ToggleButtonGroup from "@mui/material/ToggleButtonGroup";

import { columns, opsColumns } from "./trips-list-columns";
import useLocalStorage from "../../helpers/useLocalStorage";
import axios from "axios";
import ai from "../../helpers/axios";
import { formatLegTime, formatTripDate } from "../../helpers/dates";
import PageHeaderRowSearch from "../shared/page-header-row-search";
import { CreateDateFilter } from "../shared/create-date-filter";
import {
  renderCalendarEventText,
  renderCalendarLabelText,
} from "../shared/calendar-text-content";
import Loader from "../loader";
import { useDebouncedCallback } from "use-debounce";

const stageFilterOptions = [
  { label: "Active", value: "newTrip" },
  { label: "Ended", value: "ended" },
  { label: "Canceled", value: "canceled" },
  { label: "Archived", value: "archived" },
];

const paxOptions = [
  { label: "None", value: null, color: "#f44336" },
  { label: "Partial", value: "partial", color: "#ff9800" },
  { label: "Complete", value: "complete", color: "#026e02" },
];

let toastOptions = {
  position: "top-right",
  autoClose: 10000,
  hideProgressBar: false,
  closeOnClick: true,
  pauseOnHover: true,
  draggable: true,
  icon: false,
};

const DEFAULT_OPS_VIEW_CONFIG = {
  tripData: {
    label: "Trip Data",
    expanded: true,
    columns: [
      "displayStage",
      "ownerName",
      "tripTag",
      "createdAt",
      "clientName",
      "firstLeg",
      "lastLeg",
      "nextLeg",
      "operatorName",
    ],
    color: "#FFF2CC",
  },
  financials: {
    label: "Financials",
    expanded: true,
    columns: [
      "signedContractsFlightCharges",
      "clientBalance",
      "signedOFRsFlightCharges",
      "ofrBalance",
      "nextPenalty",
      "clientPayDate",
      "isFinancialsCC",
    ],
    color: "#D9EAD3",
  },
  notes: {
    label: "Notes",
    expanded: true,
    columns: [
      "operatorNotes",
      "operatorNotesAction",
      "clientNotes",
      "clientNotesAction",
      "tripNotes",
      "tripNotesAction",
    ],
    color: "#F6B092",
  },
  groundTransportation: {
    label: "Ground Transp",
    expanded: true,
    columns: [
      "groundTransportationVendor",
      "groundTransportationAmount",
      "groundTransportationAction",
      "isGroundTransportation24hCheck",
    ],
    color: "#EAD1DC",
  },
  catering: {
    label: "Catering",
    expanded: true,
    columns: [
      "cateringVendor",
      "cateringAmount",
      "cateringAction",
      "isCatering24hCheck",
    ],
    color: "#FF9900",
  },
  itinerary: {
    label: "Itinerary",
    expanded: true,
    columns: [
      "appUser",
      "viewItinerary",
      "prelim",
      "final",
      "pax",
      "is48hrWeather",
      "isRepo",
      "isInPosition",
      "isDayOf",
    ],
    color: "#EFEFEF",
  },
};
const DEFAULT_OPS_VIEW_SECTIONS_VISIBILITY = Object.keys(
  DEFAULT_OPS_VIEW_CONFIG
).reduce((acc, key) => ({ ...acc, [key]: true }), {});

const conditionalRowStyles = [
  {
    when: (row) => row.displayStage === "canceled",
    style: {
      backgroundColor: "#ffcdd2 !important", // darker red
      "&:hover": {
        cursor: "pointer",
        backgroundColor: "#ef9a9a", // slightly lighter on hover
      },
    },
  },
  {
    when: (row) => row.displayStage === "archived",
    style: {
      backgroundColor: "#ffebee !important", // lighter red
      "&:hover": {
        cursor: "pointer",
        backgroundColor: "#ffcdd2", // slightly darker on hover
      },
    },
  },
];
const opsConditionalRowStyles = [
  ...conditionalRowStyles,
  {
    when: (row) => row.isLocked,
    style: {
      backgroundColor: "#F6D992 !important",
      "&:hover": {
        cursor: "pointer",
        backgroundColor: "#F6CF92",
      },
    },
  },
];

const showToastMessage = (id, message, options) => {
  if (id) {
    toast.update(id, {
      render: message,
      ...toastOptions,
      ...options,
    });
  } else {
    return toast(message, {
      ...toastOptions,
      ...options,
    });
  }
};

const colors = [
  "#0f9ed5",
  "#f1a983",
  "#4da72e",
  "#ffff00",
  "#d86dcd",
  "#ff0000",
  "#2196F3", // Blue
  "#FF5722", // Deep Orange
  "#4CAF50", // Green
  "#9C27B0", // Purple
  "#FFC107", // Amber
  "#E91E63", // Pink
];

const assignColorsToEvents = (events) => {
  const colorMap = new Map();
  const dayEvents = new Map(); // Track events by day

  // Group events by day
  events.forEach((event) => {
    const date = new Date(event.start).toDateString();
    if (!dayEvents.has(date)) {
      dayEvents.set(date, []);
    }
    dayEvents.get(date).push(event);
  });

  // Process each day's events
  dayEvents.forEach((dailyEvents, date) => {
    const usedColorsForDay = new Set();

    // Sort events by start time
    dailyEvents.sort((a, b) => new Date(a.start) - new Date(b.start));

    dailyEvents.forEach((event) => {
      // Check colors used by previous and next day
      const eventDate = new Date(event.start);
      const prevDate = new Date(eventDate);
      const nextDate = new Date(eventDate);
      prevDate.setDate(eventDate.getDate() - 1);
      nextDate.setDate(eventDate.getDate() + 1);

      const adjacentDayEvents = [
        ...(dayEvents.get(prevDate.toDateString()) || []),
        ...(dayEvents.get(nextDate.toDateString()) || []),
      ];

      const unavailableColors = new Set([
        ...Array.from(usedColorsForDay),
        ...adjacentDayEvents.map((e) => colorMap.get(e.id)).filter(Boolean),
      ]);

      // Find first available color
      const availableColor = colors.find(
        (color) => !unavailableColors.has(color)
      );

      if (availableColor) {
        colorMap.set(event.id, availableColor);
        event.color = availableColor;
        usedColorsForDay.add(availableColor);
      } else {
        // Fallback: use least used color for this day
        const colorUsageCounts = colors.reduce((acc, color) => {
          acc[color] = 0;
          return acc;
        }, {});

        Array.from(usedColorsForDay).forEach((color) => {
          colorUsageCounts[color]++;
        });

        const leastUsedColor = Object.entries(colorUsageCounts).sort(
          ([, a], [, b]) => a - b
        )[0][0];

        colorMap.set(event.id, leastUsedColor);
        event.color = leastUsedColor;
        usedColorsForDay.add(leastUsedColor);
      }
    });
  });

  return events;
};

const reducer = (state, action) => {
  switch (action.type) {
    case "search": {
      return {
        ...state,
        query: action.query,
      };
    }
    case "dataBeganDownloading": {
      return {
        ...state,
        loading: action.payload.loadingStatus,
        // Reset trips when starting a new load
        trips: action.payload.loadingStatus ? [] : state.tableData,
      };
    }
    case "rowsDeleted": {
      return {
        ...state,
        clearSelectedRows: !state.clearSelectedRows,
        selectedRows: [],
      };
    }
    case "rowSelection": {
      return {
        ...state,
        selectedRows: action.payload.selectedRows,
        allSelected: action.payload.allSelected,
      };
    }

    case "dataDownloaded": {
      return {
        ...state,
        tableData: action?.payload?.data?.trips || [],
        loading: false,
        totalLength: action?.payload?.data?.countTotal,
        tableLoading: false, // Ensure loading is set to false
      };
    }

    case "pageChanged": {
      return {
        ...state,
        currentPage: action.payload.page,
      };
    }

    case "tableSort": {
      return {
        ...state,
        sortBy: action.payload.sortBy,
        sortDirection: action.payload.sortDirection,
        currentPage: 1,
      };
    }

    case "stageFilterChanged": {
      return {
        ...state,
        stageFilters: action.payload.values,
        currentPage: 1, // Reset to first page when filter changes
      };
    }

    case "createDatesChanged": {
      return {
        ...state,
        createDates: action.payload.values,
        currentPage: 1,
      };
    }

    case "rowsPerPageChanged": {
      return {
        ...state,
        countPerPage: action.payload.currentRowsPerPage,
        currentPage: 1,
      };
    }

    case "fieldChanged": {
      return {
        ...state,
        [action.field]: action.value,
      };
    }

    case "tableDataChanged": {
      return {
        ...state,
        tableData: [...state.tableData],
      };
    }

    case "tableDataRowChanged": {
      return {
        ...state,
        tableData: [
          ...state.tableData.map((trip) =>
            trip._id === action.payload.id
              ? { ...trip, ...action.payload.data }
              : trip
          ),
        ],
      };
    }

    case "setPopupTripInfo": {
      return {
        ...state,
        popupTripInfo: action.payload,
      };
    }

    case "setTableLoading": {
      return {
        ...state,
        tableLoading: action.payload,
      };
    }

    case "setCalendarLoading": {
      return {
        ...state,
        calendarLoading: action.payload,
      };
    }

    case "setCalendarEvents": {
      return {
        ...state,
        calendarData: action.payload, // Store in calendarData instead of trips
        calendarLoading: false,
      };
    }

    case "setCalendarDateRange": {
      return {
        ...state,
        calendarDateRange: action.payload,
      };
    }

    case "setInitialLoadComplete": {
      return {
        ...state,
        initialLoadComplete: true,
      };
    }

    case "ownerFilterChanged": {
      return {
        ...state,
        ownerFilters: action.payload.values,
        currentPage: 1, // Reset to first page when filter changes
      };
    }

    default:
      break;
  }
  return state;
};

const formatUSDAmount = (amount) => {
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
  }).format(amount);
};

const TripsListOpsViewSubHeaderColumn = ({
  column,
  config,
  toggleExpanded,
}) => {
  const handleClick = useCallback(
    (event) => {
      event.preventDefault();
      toggleExpanded(column);
    },
    [column, toggleExpanded]
  );

  return (
    <div
      className="p-2"
      style={{
        backgroundColor: config.color,
        cursor: "pointer",
      }}
      onClick={handleClick}
    >
      {config.expanded ? <CheckBoxIcon /> : <CheckBoxOutlineBlank />}
      {config.label}
    </div>
  );
};

const TripsListOpsViewSubHeader = ({ opsViewConfig, toggleOpsViewColumn }) => {
  return (
    <div className="d-flex justify-content-between align-items-center ml-20 mb-20 mt-20 gap-20">
      {Object.entries(opsViewConfig).map(([column, config], index) => (
        <TripsListOpsViewSubHeaderColumn
          key={column}
          column={column}
          config={config}
          toggleExpanded={toggleOpsViewColumn}
        />
      ))}
    </div>
  );
};

const TripsListContent = (props) => {
  const [viewType, setViewType] = useLocalStorage("viewType", "table");
  const [opsViewSectionsVisibility, setOpsViewSectionsVisibility] =
    useLocalStorage(
      "opsViewSectionsVisibility",
      DEFAULT_OPS_VIEW_SECTIONS_VISIBILITY
    );

  const toggleOpsViewColumn = useCallback(
    (column) => {
      setOpsViewSectionsVisibility({
        ...opsViewSectionsVisibility,
        [column]: !opsViewSectionsVisibility[column],
      });
    },
    [opsViewSectionsVisibility, setOpsViewSectionsVisibility]
  );

  const opsViewConfig = useMemo(() => {
    const config = { ...DEFAULT_OPS_VIEW_CONFIG };
    Object.entries(opsViewSectionsVisibility).forEach(([column, expanded]) => {
      config[column].expanded = expanded;
    });
    return config;
  }, [opsViewSectionsVisibility]);

  const initialState = {
    tableData: [],
    calendarData: [],
    trips: {
      data: [],
    },
    loading: true,
    countPerPage: 25,
    currentPage: 1,
    selectedRows: [],
    allSelected: false,
    tagFilters: [],
    totalLength: null,
    clearSelectedRows: false,

    ownerFilters: props?.defaultOwnerFilters?.length
      ? [...props.defaultOwnerFilters.map((filter) => filter.value)]
      : [],
    defaultOwnerFilters: props?.defaultOwnerFilters?.length
      ? props.defaultOwnerFilters
      : [],
    ownerFilterOptions: [],
    ownerFiltersInput: props?.defaultOwnerFilters?.length
      ? props.defaultOwnerFilters
      : [],

    selectedOwner: null,
    query: "",
    createDates: [],
    stageFilters:
      viewType === "calendar"
        ? ["newTrip", "ended"]
        : viewType === "ops"
        ? ["newTrip"]
        : [],
    sortDirection: viewType === "ops" ? "asc" : "desc",
    sortBy: viewType === "ops" ? "nextLeg" : "createdAt",
    ownerOptions: [],
    teamMembers: [],
    popupTripInfo: {},
    tableLoading: true,
    calendarLoading: true,
    calendarDateRange: null,
    initialLoadComplete: false,
  };

  const { user } = useAuth0();

  const userRoles = user && user["https://new.evojets.com/roles"];
  const isOps = user && user["https://new.evojets.com/appMetadata"]?.isOps;

  const superAdminOrAd = userRoles.some(function (role) {
    return "Super Admin" === role || "Account Director" === role;
  });

  const { getAccessTokenSilently, loginWithRedirect } = useAuth0();

  const [state, dispatch] = useReducer(reducer, initialState);

  const timerRef = useRef(null);
  const waitingRef = useRef(null);
  const initialLoadRef = useRef(false);
  const abortControllerRef = useRef(null);
  const calendarAbortControllerRef = useRef(null);

  const myView = { calendar: { labels: true } };

  const openInNewTab = (url) => {
    window.open(url, "_blank", "noreferrer");
  };

  const navigateToTrip = (id) => {
    openInNewTab(`/trips/${id}`);
  };

  const handleEventHoverIn = useCallback(
    (args) => {
      const source = args.source;
      const type = args.type;
      const selectedTrip = args.event;
      const tripDetails = state.calendarData.find((trip) => {
        return trip._id === selectedTrip.tripId;
      });

      // Only proceed if we found matching trip details
      if (!tripDetails) return;
      // Prevent open popup when moving inside popover
      if (waitingRef.current) return;
      if (source === "popover") {
        if (type === "onEventClick") {
          waitingRef.current = true;
          setTimeout(() => {
            waitingRef.current = false;
          }, 300);
        } else {
          return;
        }
      }

      // Format legs array before adding to payload
      const formattedLegs = tripDetails.itineraryData[0].legs.map((leg) => {
        const etdTime = formatLegTime(
          leg.etd,
          leg.departureTimeZone,
          "hh:mm A"
        );
        // Return a string instead of an object
        return `${formatTripDate(
          leg.departureDate,
          "M/D/YYYY"
        )} ${etdTime} | ${leg.departureAirport.code} - ${
          leg.arrivalAirport.code
        }`;
      });

      dispatch({
        type: "setPopupTripInfo",
        payload: {
          legId: selectedTrip.legId,
          id: tripDetails._id,
          owner: tripDetails.owner ? tripDetails.owner.name : "No Owner",
          title: selectedTrip.title,
          createdAt: tripDetails.createdAt,
          clientName: tripDetails.clientName || "No Client Name",
          legs: formattedLegs, // Now an array of strings instead of objects
          payments: tripDetails.clientPaymentHistory.map(
            (payment) =>
              `${formatUSDAmount(payment.amount)} - ${
                payment.paymentType || ""
              } - ${new Date(payment.paymentDate).toLocaleDateString()}`
          ),
          tripTag: tripDetails.tripTag ? "#" + tripDetails.tripTag : "",
          anchor: args.domEvent.target,
          isOpen: true,
          activeIndex: selectedTrip.legIndex,
        },
      });

      if (timerRef.current) {
        clearTimeout(timerRef.current);
      }
    },
    [state.calendarData]
  );

  const handleEventHoverOut = useCallback((args) => {
    const source = args.source;
    if (source === "popover") return;

    timerRef.current = setTimeout(() => {
      dispatch({
        type: "setPopupTripInfo",
        payload: {
          isOpen: false,
        },
      });
    }, 200);
  }, []);

  const handleEventClick = useCallback(
    (args) => {
      handleEventHoverIn(args);
    },
    [handleEventHoverIn]
  );

  const onMouseEnter = useCallback(() => {
    if (timerRef.current) {
      clearTimeout(timerRef.current);
    }
  }, []);

  const onMouseLeave = useCallback(() => {
    timerRef.current = setTimeout(() => {
      dispatch({
        type: "setPopupTripInfo",
        payload: {
          isOpen: false,
        },
      });
    }, 200);
  }, []);

  const getData = useCallback(
    async (
      dbQuery,
      loadSilently,
      stageFilters,
      sortBy,
      sortDirection,
      ownerFilters,
      createDates
    ) => {
      if (viewType === "calendar") return;

      abortControllerRef.current?.abort();
      abortControllerRef.current = new AbortController();
      const signal = abortControllerRef.current.signal;

      dispatch({ type: "setTableLoading", payload: true });

      try {
        const token = await getAccessTokenSilently();
        const response = await ai.auth(token).get(`/api/trips/list`, {
          signal,
          params: {
            query: dbQuery,
            page: state.currentPage,
            countPerPage: state.countPerPage,
            stageFilters: JSON.stringify(stageFilters),
            sortBy,
            sortDirection,
            ownerFilters: JSON.stringify(ownerFilters),
            createDates: JSON.stringify(createDates ?? []),
            currentDate: new Date(),
          },
        });

        if (response?.data) {
          if (["table", "ops"].includes(viewType)) {
            dispatch({
              type: "dataDownloaded",
              payload: {
                data: response.data,
              },
            });
          }
        }
      } catch (error) {
        if (axios.isCancel(error)) return;
        console.error("Error fetching table data:", error);
        toast.error("Failed to load trips");
      } finally {
        if (!signal.aborted) {
          dispatch({ type: "setTableLoading", payload: false });
        }
      }
    },
    [viewType, getAccessTokenSilently, state.currentPage, state.countPerPage]
  );

  // funcs for calendar view

  const fetchCalendarData = useCallback(
    async (start, end, context, query) => {
      if (!start || !end) {
        return;
      }

      calendarAbortControllerRef.current?.abort();
      calendarAbortControllerRef.current = new AbortController();
      const signal = calendarAbortControllerRef.current.signal;

      const toastId = showToastMessage(null, "Events are loading...", {
        icon: "📆",
        id: "eventToast",
        theme: "dark",
      });

      try {
        const token = await getAccessTokenSilently();
        const params = new URLSearchParams({
          dateRangeStart: start,
          dateRangeEnd: end,
          stageFilters: JSON.stringify(state.stageFilters),
          ownerFilters: JSON.stringify(state.ownerFilters),
        });

        if (query) {
          params.append("query", query.trim());
        }

        const response = await ai
          .auth(token)
          .get("/api/trips/calendarViewList", { params, signal });

        if (response?.data?.trips) {
          dispatch({
            type: "setCalendarEvents",
            payload: response.data.trips,
          });
          showToastMessage(toastId, "Events loaded.", { type: "success" });
        }
      } catch (error) {
        if (axios.isCancel(error)) {
          toast.dismiss(toastId);
          return;
        }
        console.error("Error fetching calendar data:", error);
        showToastMessage(toastId, "Failed to load events", "error");
      }
    },
    [
      getAccessTokenSilently,
      state.stageFilters,
      state.ownerFilters,
      state.query,
    ]
  );

  const debouncedFetchCalendarData = useDebouncedCallback((start, end, context, query) => {
    return fetchCalendarData(start, end, context, query);
  }, 100);

  const handleCalendarNavigate = useCallback(
    (args) => {
      if (viewType !== "calendar") {
        return;
      }

      const { firstDay, lastDay } = args;

      // Update ref immediately
      calendarDateRangeRef.current = {
        start: moment(firstDay).format("YYYY-MM-DD"),
        end: moment(lastDay).format("YYYY-MM-DD"),
      };
      dispatch({
        type: "setCalendarDateRange",
        payload: calendarDateRangeRef.current,
      });
    },
    [viewType]
  );

  const handleChangeViewType = useCallback(
    (event, newViewType) => {
      if (!newViewType) {
        return;
      }

      setViewType(newViewType);
      if (newViewType === "calendar") {
        // Set default filters for calendar view
        dispatch({
          type: "stageFilterChanged",
          payload: { values: ["newTrip", "ended"] },
        });
      } else {
        if (newViewType === "ops") {
          dispatch({
            type: "stageFilterChanged",
            payload: { values: ["newTrip"] },
          });
          dispatch({
            type: "tableSort",
            payload: {
              sortBy: "nextLeg",
              sortDirection: "asc",
            },
          });
        } else {
          dispatch({
            type: "stageFilterChanged",
            payload: { values: [] },
          });
          dispatch({
            type: "tableSort",
            payload: {
              sortBy: "createdAt",
              sortDirection: "desc",
            },
          });
        }
        if (viewType === "calendar") {
          dispatch({ type: "setTableLoading", payload: true });
          dispatch({ type: "setCalendarEvents", payload: [] });

          getData(
            state.query,
            true,
            state.stageFilters,
            state.sortBy,
            state.sortDirection,
            state.ownerFilters,
            state.createDates
          );
        }
      }
    },
    [
      viewType,
      setViewType,
      getData,
      state.query,
      state.stageFilters,
      state.sortBy,
      state.sortDirection,
      state.ownerFilters,
      state.createDates,
    ]
  );

  // end funcs for calendar view

  // Simplify handleSort to directly use the provided direction
  const handleSort = (column, sortDirection) => {
    // Set loading immediately
    dispatch({ type: "setTableLoading", payload: true });

    const newDirection =
      column.selector === state.sortBy
        ? state.sortDirection === "asc"
          ? "desc"
          : "asc"
        : "desc";

    // Update sort state first
    dispatch({
      type: "tableSort",
      payload: {
        sortBy: column.selector,
        sortDirection: newDirection,
      },
    });
  };

  const calendarDateRangeRef = useRef(null);

  useEffect(() => {
    if (["table", "ops"].includes(viewType)) {
      getData(
        state.query,
        true,
        state.stageFilters,
        state.sortBy,
        state.sortDirection,
        state.ownerFilters,
        state.createDates
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    state.query,
    state.sortBy,
    state.sortDirection,
    state.currentPage,
    state.countPerPage,
    state.stageFilters,
    state.createDates,
    viewType,
  ]);

  const handlePageChange = (page, totalRows) => {
    dispatch({
      type: "pageChanged",
      payload: {
        page,
        totalRows,
      },
    });
  };

  const handleRowsPerPageChange = (currentRowsPerPage, currentPage) => {
    dispatch({
      type: "rowsPerPageChanged",
      payload: {
        currentRowsPerPage,
        currentPage,
      },
    });
  };

  const loadOwnerOptions = async () => {
    try {
      const token = await getAccessTokenSilently();
      const { data } = await ai.auth(token).get(`/api/users/roles/sales`);

      if (data.users?.length) {
        const results = data.users.map((user) => ({
          value: user.user_id,
          label: user.name,
          email: user.email,
        }));

        dispatch({
          type: "fieldChanged",
          field: "ownerOptions",
          value: results,
        });

        const ownerFilterOptions = [...results];
        ownerFilterOptions.unshift({ label: "Unassigned", value: "null" }); // Use "null" string to match backend expectation

        dispatch({
          type: "fieldChanged",
          field: "ownerFilterOptions",
          value: ownerFilterOptions,
        });

        return results;
      }
      return [];
    } catch (error) {
      console.error("Error loading owner options:", error);
      toast.error("Failed to load owner options");
      return [];
    }
  };

  const handleOwnerFilterChange = (selectedValues) => {
    const values = selectedValues?.length
      ? selectedValues.map(({ value }) => value)
      : [];

    dispatch({
      type: "ownerFilterChanged",
      payload: { values },
    });

    dispatch({
      type: "fieldChanged",
      field: "ownerFiltersInput",
      value: selectedValues,
    });

    getData(
      state.query,
      true,
      state.stageFilters,
      state.sortBy,
      state.sortDirection,
      values,
      state.createDates
    );
  };

  const handleCreateDatesChange = (values) => {
    dispatch({
      type: "createDatesChanged",
      payload: { values: values ?? [] },
    });
  };

  const handleStageFilterChange = (selectedOptions) => {
    const values = selectedOptions?.length
      ? selectedOptions.map((option) => option.value)
      : [];

    dispatch({
      type: "stageFilterChanged",
      payload: { values },
    });
  };

  useEffect(() => {
    if (["table", "ops"].includes(viewType) && !initialLoadRef.current) {
      initialLoadRef.current = true;
      dispatch({ type: "setTableLoading", payload: true });
      getData(
        state.query,
        false,
        state.stageFilters,
        state.sortBy,
        state.sortDirection,
        state.ownerFilters,
        state.createDates
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [viewType]);

  useEffect(() => {
    const keyDown = (event) => {
      if (viewType !== "ops") {
        return;
      }

      const tableContainer = document.querySelector(
        ".trips-list-ops-view-table"
      );

      // Home
      if (event.keyCode === 36) {
        event.preventDefault();
        tableContainer.scrollLeft = 0;
      }
      // End
      if (event.keyCode === 35) {
        event.preventDefault();
        tableContainer.scrollLeft =
          tableContainer.scrollWidth - tableContainer.clientWidth;
      }
    };

    document.addEventListener("keydown", keyDown);

    return () => {
      document.removeEventListener("keydown", keyDown);
    };
  }, [viewType]);

  useEffect(() => {
    document.title = `${
      props.pageTitle ? props.pageTitle : "Trips"
    } | evoJets HQ`;
    loadOwnerOptions(); // Load owner options when component mounts
  }, []);

  useEffect(() => {
    if (calendarDateRangeRef.current) {
      dispatch({
        type: "setCalendarDateRange",
        payload: calendarDateRangeRef.current,
      });
      calendarDateRangeRef.current = null;
    }
  }, []);

  useEffect(() => {
    if (viewType === "calendar" && state.calendarDateRange) {
      debouncedFetchCalendarData(
        state.calendarDateRange.start,
        state.calendarDateRange.end,
        "USE EFFECT FOR FILTERS AND SEARCH"
      );
    }
  }, [
    viewType,
    state.stageFilters,
    state.ownerFilters,
    state.calendarDateRange,
  ]);

  const searchInputRef = useRef("");
  const isSearchingRef = useRef(false);

  // Helper function to handle both table and calendar searches
  const executeSearch = (query) => {
    // Prevent duplicate searches
    if (isSearchingRef.current) return;

    isSearchingRef.current = true;

    if (query === state.query) {
      return;
    }

    dispatch({ type: "search", query });
    dispatch({ type: "setTableLoading", payload: true });

    if (viewType === "calendar" && state.calendarDateRange) {
      debouncedFetchCalendarData(
        state.calendarDateRange.start,
        state.calendarDateRange.end,
        "SEARCH FUNCTION",
        query
      ).finally(() => {
        isSearchingRef.current = false;
      });
    } else {
      // Reset the flag after a short delay for table view
      setTimeout(() => {
        isSearchingRef.current = false;
      }, 100);
    }
  };

  // New handler for tracking input value without triggering search
  const handleInputChange = (e) => {
    searchInputRef.current = e.target.value.toLowerCase();
  };

  // Modified to handle Enter key instead of Tab
  const handleKeyDown = (e) => {
    if (e.keyCode === 13) {
      e.preventDefault(); // Prevent form submission
      const query = searchInputRef.current;
      executeSearch(query);
    }
  };

  useEffect(() => {
    // Cleanup function
    return () => {
      // Clear any existing toasts when component unmounts
      toast.dismiss();
      // Clear any pending calendar date range updates
      calendarDateRangeRef.current = null;
    };
  }, []);

  const handleBlur = () => {
    // Only search on blur if the current value is different from the last searched value
    const query = searchInputRef.current;
    if (query !== state.query && !isSearchingRef.current) {
      executeSearch(query);
    }
  };

  const handleUpdateTrip = useCallback(
    async (id, data) => {
      const token = await getAccessTokenSilently().catch((e) => {
        console.error(e);
        return loginWithRedirect();
      });

      try {
        const response = await ai.auth(token).patch(`/api/trips/${id}`, data);

        if (response?.data?.data) {
          const { _id, ...tripDetails } = response.data.data;

          dispatch({
            type: "tableDataRowChanged",
            payload: {
              id: _id,
              data: tripDetails,
            },
          });
        }

        toast.success("Trip was updated successfully");
      } catch (error) {
        toast.error("Failed to update trip");
        console.error(error);
      }
    },
    [getAccessTokenSilently, loginWithRedirect]
  );

  const handleToggleTripRowCheckbox = useCallback(
    (event) => {
      const { name, checked } = event.target;
      const [, field, id] = name.match(/^([a-zA-Z_]\w*)\[(\w+)\]$/) || [];

      if (field && id) {
        handleUpdateTrip(id, {
          [field]: checked,
        });
      }
    },
    [handleUpdateTrip]
  );

  const handleChangeTripRowSelect = useCallback(
    (option, { name }) => {
      const [, field, id] = name.match(/^([a-zA-Z_]\w*)\[(\w+)\]$/) || [];

      if (field && id) {
        handleUpdateTrip(id, {
          [field]: option?.value || null,
        });
      }
    },
    [handleUpdateTrip]
  );

  const opsViewColumns = useMemo(
    () =>
      opsColumns.map((config) => {
        const section = Object.values(opsViewConfig).find(({ columns }) =>
          columns.includes(config.selector)
        );
        const omit =
          !!(section && !section.expanded) ||
          (!isOps &&
            [
              "operatorNotesAction",
              "clientNotesAction",
              "tripNotesAction",
              "groundTransportationAction",
              "cateringAction",
              "viewItinerary",
            ].includes(config.selector));

        const updatedConfig = {
          ...config,
          omit,
        };

        if (config.selector === "isLocked") {
          updatedConfig.cell = (row) => (
            <div>
              <Checkbox
                checked={row.isLocked}
                onChange={handleToggleTripRowCheckbox}
                name={`isLocked[${row._id}]`}
                disabled={!isOps}
              />
            </div>
          );
        }

        if (
          [
            "isFinancialsCC",
            "isGroundTransportation24hCheck",
            "isCatering24hCheck",
            "is48hrWeather",
            "isRepo",
            "isInPosition",
            "isDayOf",
          ].includes(config.selector)
        ) {
          updatedConfig.cell = (row) => (
            <div>
              <Checkbox
                checked={row[config.selector]}
                onChange={handleToggleTripRowCheckbox}
                name={`${config.selector}[${row._id}]`}
                disabled={row.isLocked || !isOps}
                sx={{
                  color: row[config.selector] ? "green" : "red",
                  "&.Mui-checked": {
                    color: row[config.selector] ? "green" : "red",
                  },
                }}
              />
            </div>
          );
        }

        if (config.selector === "pax") {
          updatedConfig.cell = (row) => (
            <div className="w-100" data-testid={`ops-view-pax-select-${row._id}`}>
              <Select
                options={paxOptions}
                placeholder="Select Pax"
                name={`pax[${row._id}]`}
                value={
                  paxOptions.find((option) => option.value === row.pax) ||
                  paxOptions[0]
                }
                onChange={handleChangeTripRowSelect}
                menuPosition="fixed"
                menuPortalTarget={document.body}
                closeMenuOnSelect
                styles={{
                  option: (styles, { data, isFocused, isSelected }) => ({
                    ...styles,
                    color: isSelected || isFocused ? "#FFFFFF" : data.color,
                    backgroundColor:
                      isSelected || isFocused ? data.color : "#FFFFFF",
                  }),
                  singleValue: (styles, { data }) => ({
                    ...styles,
                    color: data.color,
                  }),
                }}
                isDisabled={row.isLocked || !isOps}
              />
            </div>
          );
        }

        return updatedConfig;
      }),
    [
      opsViewConfig,
      handleToggleTripRowCheckbox,
      handleChangeTripRowSelect,
      isOps,
    ]
  );

  const handleRowClick = useCallback(
    (row, event) => {
      event.preventDefault();

      dispatch({
        type: "fieldChanged",
        field: "selectedRows",
        value: state.selectedRows.find(
          (selectedRow) => selectedRow._id === row._id
        )
          ? [...state.selectedRows].filter(
              (selectedRow) => selectedRow._id !== row._id
            )
          : [...state.selectedRows, row],
      });
      dispatch({ type: "tableDataChanged" });
    },
    [state.selectedRows]
  );

  const isSelectableRowSelected = useCallback(
    (row) =>
      state.selectedRows.find((selectedRow) => selectedRow._id === row._id),
    [state.selectedRows]
  );

  return (
    <Fragment>
      <PageHeaderRowSearch
        h1={`${props.pageTitle ? props.pageTitle : "Trips"}`}
        pretitle="Business"
        breadcrumbs={[{ link: "/trips", label: "Trips" }]}
        extraClasses="page-header-trips-l1"
        rightColumn={
          <div id="trip-actions-row">
            <CreateDateFilter
              className="create-date-filter"
              value={state.createDates}
              onChange={handleCreateDatesChange}
            />

            <input
              type="text"
              onChange={handleInputChange}
              onKeyDown={handleKeyDown}
              onBlur={handleBlur}
              autoComplete={"off"}
              className="lead-search ml-0"
              placeholder={"Search Trips"}
              id="searchFlights"
            />

            <Select
              options={stageFilterOptions}
              isMulti
              className="stage-filter"
              placeholder="Filter by Stage"
              value={stageFilterOptions.filter((option) =>
                state.stageFilters.includes(option.value)
              )}
              onChange={handleStageFilterChange}
              // Add these props for better UX
              isClearable={true}
              closeMenuOnSelect={false}
              defaultValue={[...state.stageFilters]}
            />
            {superAdminOrAd && (
              <Select
                options={state.ownerFilterOptions}
                isMulti
                className="owner-filter"
                placeholder="Filter by Owner"
                value={state.ownerFiltersInput}
                onChange={handleOwnerFilterChange}
                isClearable={true}
                closeMenuOnSelect={false}
              />
            )}

            <ToggleButtonGroup
              value={viewType}
              exclusive
              onChange={handleChangeViewType}
              aria-label="Trip List View"
              size="small"
            >
              <ToggleButton value="table" aria-label="left aligned" disabled={viewType === "table"}>
                <span className="material-icons">table_view</span>
              </ToggleButton>
              <ToggleButton value="calendar" aria-label="centered" disabled={viewType === "calendar"}>
                <span className="material-icons">calendar_month</span>
              </ToggleButton>
              <ToggleButton value="ops" aria-label="right aligned" disabled={viewType === "ops"}>
                <span className="material-icons">build</span>
              </ToggleButton>
            </ToggleButtonGroup>
          </div>
        }
      />

      {viewType === "table" && (
        <div
          className={`view-transition ${
            viewType === "table" ? "view-visible" : ""
          }`}
        >
          <div className="server-pagination-table-container trips-list-table-container">
            <StickyHeaderDataTable
              columns={columns}
              data={state.tableData}
              pagination
              paginationServer
              paginationTotalRows={state.totalLength}
              paginationPerPage={state.countPerPage}
              paginationDefaultPage={state.currentPage}
              paginationRowsPerPageOptions={[25, 50, 100, 150, 200, 250, 500]}
              onChangePage={handlePageChange}
              onChangeRowsPerPage={handleRowsPerPageChange}
              sortServer
              onSort={handleSort}
              // sortField={state.sortBy}
              defaultSortFieldId={state.sortBy}
              sortDirection={state.sortDirection}
              progressPending={state.tableLoading}
              progressComponent={<Loader />}
              persistTableHead
              highlightOnHover
              responsive
              className="server-pagination-table"
              conditionalRowStyles={conditionalRowStyles}
            />
          </div>
        </div>
      )}

      {viewType === "ops" && (
        <div
          className={`view-transition ${
            viewType === "ops" ? "view-visible" : ""
          }`}
        >
          <div className="server-pagination-table-container trips-list-table-container trips-list-ops-view-table-container">
            <StickyHeaderDataTable
              columns={opsViewColumns}
              subHeader
              subHeaderWrap={true}
              subHeaderAlign="left"
              customStyles={{
                subHeader: {
                  style: {
                    minHeight: "unset",
                    padding: 0,
                  },
                },
              }}
              subHeaderComponent={
                <TripsListOpsViewSubHeader
                  opsViewConfig={opsViewConfig}
                  toggleOpsViewColumn={toggleOpsViewColumn}
                />
              }
              data={state.tableData}
              pagination
              paginationServer
              paginationTotalRows={state.totalLength}
              paginationPerPage={state.countPerPage}
              paginationDefaultPage={state.currentPage}
              paginationRowsPerPageOptions={[25, 50, 100, 150, 200, 250, 500]}
              onChangePage={handlePageChange}
              onChangeRowsPerPage={handleRowsPerPageChange}
              sortServer
              onSort={handleSort}
              defaultSortFieldId={state.sortBy}
              sortDirection={state.sortDirection}
              progressPending={state.tableLoading}
              progressComponent={<Loader />}
              persistTableHead
              highlightOnHover
              responsive
              className="server-pagination-table trips-list-ops-view-table"
              conditionalRowStyles={opsConditionalRowStyles}
              onRowClicked={handleRowClick}
              selectableRowSelected={isSelectableRowSelected}
              selectableRowsHighlight
            />
          </div>
        </div>
      )}

      {viewType === "calendar" && (
        <div
          className={`view-transition ${
            viewType === "calendar" ? "view-visible" : ""
          }`}
        >
          {false ? (
            <div className="calendar-loader-container">
              <Loader text="Loading calendar..." />
            </div>
          ) : (
            <Eventcalendar
              clickToCreate={false}
              dragToCreate={false}
              dragToMove={false}
              refreshControl={false}
              dragToResize={false}
              eventDelete={false}
              showEventTooltip={false}
              renderLabelContent={renderCalendarLabelText}
              renderEventContent={renderCalendarEventText}
              onEventHoverIn={handleEventHoverIn}
              onEventHoverOut={handleEventHoverOut}
              onPageLoading={handleCalendarNavigate}
              data={(() => {
                const legEvents = state.calendarData
                  .flatMap((trip) => {
                    if (!trip.itineraryData?.[0]?.legs?.length) return [];

                    const totalLegs = trip.itineraryData[0].legs.length;

                    return trip.itineraryData[0].legs.map((leg, index) => {
                      // departureDate is a date-only marker at UTC midnight and
                      // etd/eta are true instants. Take the day in UTC and read
                      // BOTH clocks in the departure airport's zone, so the block
                      // spans the real flight time; reading eta in the arrival
                      // zone would distort the duration across timezones. The
                      // moment({...}) below builds a browser-local Date, which is
                      // what Eventcalendar plots, so the event shows the same
                      // departure-local day and time to every viewer.
                      const legZone = leg.departureTimeZone || "America/New_York";
                      const legDay = moment.utc(leg.departureDate);
                      const etdInZone = leg.etd ? moment.tz(leg.etd, legZone) : null;
                      const etaInZone = leg.eta ? moment.tz(leg.eta, legZone) : null;

                      const localStart = moment({
                        year: legDay.year(),
                        month: legDay.month(),
                        date: legDay.date(),
                        hour: etdInZone ? etdInZone.hour() : 0,
                        minute: etdInZone ? etdInZone.minute() : 0,
                      }).toDate();

                      // Calculate end time - if it crosses midnight, cap it at end of departure day
                      const localEnd = moment(localStart).set({
                        hour: etaInZone ? etaInZone.hour() : 0,
                        minute: etaInZone ? etaInZone.minute() : 0,
                      });

                      // If end time is before start time, it means it crossed midnight
                      // In this case, set end time to 23:59:59 of start date
                      if (localEnd.isBefore(moment(localStart))) {
                        localEnd.endOf("day");
                      }

                      return {
                        start: localStart,
                        end: localEnd.toDate(),
                        title: `(Leg ${index + 1} of ${totalLegs}) ${
                          trip.clientName || "No Client"
                        } | ${leg.departureAirport.code}-${
                          leg.arrivalAirport.code
                        }`,
                        id: leg._id,
                        legId: leg._id,
                        tripId: trip._id,
                        legIndex: index,
                      };
                    });
                  })
                  .filter(Boolean);

                return assignColorsToEvents(legEvents);
              })()}
              view={myView}
              onEventClick={handleEventClick}
              theme="ios"
              themeVariant="light"
              fuseeffect
              // Add these props
              dataTimezone="UTC"
              displayTimezone="local"
              // Force monthly view
              renderMode="month"
              calendarScroll="month"
              calendarType="month"
            />
          )}

          <Popup
            display="anchored"
            isOpen={state.popupTripInfo.isOpen}
            anchor={state.popupTripInfo.anchor}
            touchUi={false}
            showOverlay={false}
            contentPadding={false}
            closeOnOverlayClick={false}
            width={400}
            cssClass="md-tooltip"
          >
            <div onMouseEnter={onMouseEnter} onMouseLeave={onMouseLeave}>
              <div className="md-tooltip-header">
                <span className="md-tooltip-header-title">
                  {state.popupTripInfo.title}
                </span>
                <Button
                  variant="outline"
                  color="primary"
                  className="md-tooltip-view-button"
                  onClick={() => navigateToTrip(state.popupTripInfo.id)}
                >
                  View
                </Button>
              </div>

              <div className="md-tooltip-info">
                <div className="md-tooltip-title">
                  Owner:{" "}
                  <span className="md-tooltip-text">
                    {state.popupTripInfo.owner}
                  </span>
                </div>
                <div className="md-tooltip-title">
                  Created at:{" "}
                  <span className="md-tooltip-text">
                    {new Date(state.popupTripInfo.createdAt).toLocaleString()}
                  </span>
                </div>
                <div className="md-tooltip-title">
                  Client:{" "}
                  <span className="md-tooltip-text">
                    {state.popupTripInfo.clientName}
                  </span>
                </div>
                <div className="md-tooltip-title">
                  Trip:{" "}
                  <span className="md-tooltip-text">
                    {state.popupTripInfo.tripTag}
                  </span>
                </div>
                <div className="md-tooltip-title">Legs:</div>
                <ul>
                  {state.popupTripInfo &&
                    state.popupTripInfo.legs &&
                    state.popupTripInfo.legs.map((leg, i) => (
                      <li
                        className="md-tooltip-text"
                        key={`${state.popupTripInfo.id}-leg-${i}`}
                        style={{
                          color:
                            i === state.popupTripInfo.activeIndex
                              ? "var(--evoblue)"
                              : "black",
                          fontWeight:
                            i === state.popupTripInfo.activeIndex
                              ? "bold"
                              : "normal",
                        }}
                      >
                        {leg}
                      </li>
                    ))}
                </ul>
                {state.popupTripInfo.payments &&
                  state.popupTripInfo.payments.length && (
                    <>
                      <div className="md-tooltip-title">Payments:</div>
                      <ul>
                        {state.popupTripInfo.payments.map((pay, i) => (
                          <li className="md-tooltip-text" key={i}>
                            {pay}
                          </li>
                        ))}
                      </ul>
                    </>
                  )}
              </div>
            </div>
          </Popup>
        </div>
      )}
    </Fragment>
  );
};

export default TripsListContent;
